<template>
  <AppShell :wide="hasSideAds && mode === 'search'">
    <section class="hero">
      <h1>{{ pageTitle }}</h1>
      <p class="sub">{{ modeHint }}</p>
      <form class="dialog" @submit.prevent="send">
        <textarea
          v-model="keyword"
          :rows="mode === 'search' || mode === 'price' ? 2 : 5"
          :placeholder="placeholder"
          aria-label="输入内容"
        />
        <div class="dialog-bar">
          <div v-if="mode === 'bom'" class="mode-pick" @click.stop>
            <button type="button" class="mode-btn" :aria-expanded="tplOpen ? 'true' : 'false'" @click="openTemplates">
              <Icon name="server" />
              {{ pickedTpl || '选择模板' }}
              <Icon name="chevron" class="caret" />
            </button>
            <ul v-if="tplOpen" class="mode-menu tpl-menu">
              <li v-if="!templateBoards.length"><span class="tpl-empty">暂无模板</span></li>
              <li v-for="board in templateBoards" :key="board.id">
                <button type="button" :class="{ on: pickedTpl === board.name }" @click="pickTemplate(board)">
                  <span class="mode-copy">
                    <strong>{{ board.name }}</strong>
                    <small>{{ board.total }} {{ board.currency }}</small>
                  </span>
                </button>
              </li>
            </ul>
          </div>
          <label v-if="mode === 'bid'" class="attach">
            {{ bidFile ? bidFile.name : '上传文件' }}
            <input type="file" accept=".txt,.md,.pdf,.docx" @change="onBidFile" />
          </label>
          <button type="submit" class="send" :disabled="bomLoading || loading || priceLoading || adviseLoading || bidLoading">{{ sendLabel }}</button>
        </div>
      </form>
      <p v-if="mode === 'bid' && bidFile && !loggedIn" class="bid-login">
        文件已上传。
        <router-link to="/account?mode=login">登录邮箱账号</router-link>
        后才能分析。
      </p>
      <div v-if="mode === 'search'" class="tool-panel">
      <div class="filters">
        <label class="check"><input v-model="domestic" type="checkbox" /> 国产卡</label>
        <label>显存≥ <input v-model="memoryMin" type="number" min="0" placeholder="96" /> GB</label>
        <label>FP16≥ <input v-model="fp16Min" type="number" min="0" placeholder="300" /> T</label>
        <label>FP8≥ <input v-model="fp8Min" type="number" min="0" placeholder="1000" /> T</label>
      </div>
      <div class="presets">
        <button type="button" class="chip" :class="{ on: presetOn === 'domestic96' }" @click="applyPreset('domestic96')">国产 · 显存≥96GB</button>
        <button type="button" class="chip" :class="{ on: presetOn === 'fp16_300' }" @click="applyPreset('fp16_300')">FP16≥300T</button>
        <button type="button" class="chip" :class="{ on: presetOn === 'fp8_1000' }" @click="applyPreset('fp8_1000')">FP8≥1000T</button>
        <button type="button" class="chip" :class="{ on: vendor === '华为昇腾' }" @click="applyPreset('ascend')">华为昇腾</button>
        <button type="button" class="chip" :class="{ on: vendor === '海光' }" @click="applyPreset('hygon')">海光</button>
        <button type="button" class="chip" :class="{ on: vendor === '燧原科技' }" @click="applyPreset('enflame')">燧原</button>
        <button type="button" class="chip" @click="applyPreset('clear')">清空条件</button>
      </div>
      <p v-if="filterHint" class="filter-hint">当前筛选：{{ filterHint }}</p>
      <div class="hot-keys">
        <span class="label">热门：</span>
        <button v-for="item in examples" :key="item" type="button" class="chip" @click="useExample(item)">
          {{ item }}
        </button>
      </div>
      </div>
    </section>

    <section v-if="mode === 'search' && !searched && latestArticles.length" class="latest-articles">
      <div class="latest-head">
        <h2>最新资讯</h2>
        <router-link to="/articles">全部资讯</router-link>
      </div>
      <ul>
        <li v-for="item in latestArticles" :key="item.id">
          <router-link :to="`/articles/${encodeURIComponent(item.slug)}`">{{ item.title }}</router-link>
          <span class="meta">
            <template v-if="item.published_at">{{ formatArticleTime(item.published_at) }}</template>
            <template v-if="item.summary"> · {{ item.summary }}</template>
          </span>
        </li>
      </ul>
    </section>

    <div v-if="mode !== 'search'" class="stage">
      <section v-if="mode === 'bom' && (bomQuote || bomError || bomGate)" class="bom-result">
        <CreditNext v-if="bomGate" :status="bomGate" :message="bomError" />
        <p v-else-if="bomError" class="error">{{ bomError }}</p>
        <template v-else-if="bomQuote">
          <p class="bom-lead">{{ bomQuote.understand }}</p>
          <div class="bom-head">
            <h2>{{ bomQuote.template_name || '按组件组合' }} · {{ bomQuote.total }} {{ bomQuote.currency }}</h2>
            <button type="button" class="bom-dl" :disabled="bomDownloading" @click="downloadBomExcel">
              {{ bomDownloading ? '下载中…' : '下载' }}
            </button>
          </div>
          <p v-if="bomDownloadError" class="error bom-dl-err">{{ bomDownloadError }}</p>
          <p v-if="bomQuote.note" class="bom-note">{{ bomQuote.note }}</p>
          <ul>
            <li v-for="item in bomQuote.items" :key="item.id">
              <span>{{ item.category ? catLabel(item.category) + ' · ' : '' }}{{ item.name }}</span>
              <strong>{{ item.unit_price }} {{ item.currency }}</strong>
            </li>
          </ul>
          <p v-if="bomQuote.charged" class="charge">本次已扣 1 积分</p>
          <QuoteAsk :key="bomInquirySummary" kind="bom" :summary="bomInquirySummary" :detail="bomInquiryDetail" />
        </template>
      </section>
      <PriceWorkspace v-if="mode === 'price'" ref="price" embedded panel="card" />
      <BidWorkspace v-else-if="mode === 'bid'" ref="bid" embedded />
      <AdviseWorkspace v-else-if="mode === 'advise'" ref="advise" embedded />
    </div>

    <div v-if="mode === 'search'" id="search-results" class="results" :class="{ narrow: !hasSideAds }">
      <CreditNext v-if="gateStatus" :status="gateStatus" :message="error" />
      <p v-else-if="error" class="error">{{ error }}</p>
      <QueryProcessRail
        v-if="processSteps.length"
        :steps="processSteps"
        :active="loading"
        :title="loading ? '正在规划并检索卡库…' : '本次检索过程'"
      />
      <div v-if="searched && !loading && !gateStatus && !error" class="results-layout" :class="resultsLayoutClass">
        <SearchAdRail side="left" :items="adsLeft" class="rail-left" @close="dismissAds" />
        <section class="results-main">
          <div class="section-head">
            <h2>搜索结果</h2>
            <span class="result-line">共 <b>{{ resultTotal }}</b> 款</span>
            <span v-if="chargeTip" class="charge">{{ chargeTip }}</span>
            <div class="head-actions">
              <button type="button" class="clear" @click="clearSearch">清除</button>
            </div>
          </div>
          <p v-if="preview" class="preview-note">
            未登录仅展示 {{ cards.length }} 款的部分参数<span v-if="resultTotal > cards.length">（共 {{ resultTotal }} 款）</span>。
            <router-link to="/account?mode=login">登录</router-link>
            后查看完整数据。
          </p>
          <p v-if="!cards.length" class="empty">没有找到匹配的算力卡。</p>

          <article v-for="card in cards" :key="card.id" class="result">
            <div class="title-row">
              <h3>{{ card.name }}</h3>
              <div v-if="card.metrics" class="badges">
                <span v-if="card.metrics.is_domestic" class="badge on">国产</span>
                <span class="badge">{{ card.metrics.memory_gb != null ? card.metrics.memory_gb + 'GB' : '—' }}</span>
                <span class="badge">{{ card.metrics.fp16_tflops != null ? 'FP16 ' + card.metrics.fp16_tflops + 'T' : '—' }}</span>
                <span v-if="card.metrics.fp8_tflops != null" class="badge">FP8 {{ card.metrics.fp8_tflops }}T</span>
                <span class="badge">{{ card.metrics.tdp_w != null ? card.metrics.tdp_w + 'W' : '—' }}</span>
              </div>
              <button
                type="button"
                class="compare-add"
                :class="{ on: inTray(card.id) }"
                :disabled="trayFull && !inTray(card.id)"
                @click="toggleTray(card)"
              >{{ inTray(card.id) ? '已加入对比' : '加入对比' }}</button>
            </div>
            <div v-if="highlights(card).length" class="spec-cards">
              <div v-for="(attr, index) in highlights(card)" :key="index" class="spec-card">
                <div class="k">{{ attr.field_label }}</div>
                <div class="v" :class="{ blue: index === 0 }">{{ attr.field_value }}</div>
              </div>
            </div>
            <table v-if="rest(card).length" class="specs">
              <tbody>
                <tr v-for="(attr, index) in rest(card)" :key="'r' + index">
                  <th>{{ attr.field_label }}</th>
                  <td>{{ attr.field_value }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="card.prices && card.prices.length" class="prices">
              <h4>卡价库</h4>
              <table class="specs">
                <tbody>
                  <tr v-for="price in card.prices" :key="price.id">
                    <th>{{ price.variant_name || card.name }}</th>
                    <td>
                      <span v-if="price.msrp">官方 {{ price.msrp }}{{ price.currency ? ` ${price.currency}` : '' }}</span>
                      <span v-if="price.market_price"> · 市场 {{ price.market_price }}{{ price.currency ? ` ${price.currency}` : '' }}</span>
                      <span v-if="price.secondhand_price"> · 二手 {{ price.secondhand_price }}{{ price.currency ? ` ${price.currency}` : '' }}</span>
                      <span v-if="price.price_month" class="muted"> · {{ price.price_month }}</span>
                      <span v-if="price.source_label" class="muted"> · {{ price.source_label }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="card.locked && hiddenAttrCount(card) > 0" class="preview-more">
              另有 {{ hiddenAttrCount(card) }} 项参数，登录后可见。
            </p>
            <div class="discuss-row">
              <router-link
                class="discuss"
                :to="{ path: '/forum', query: { compose: '1', card_id: String(card.id), card_name: card.name, title: card.name } }"
              >讨论这张卡</router-link>
            </div>
          </article>

          <RelatedArticles
            :card-ids="cards.map((c) => c.id)"
            :keyword="keyword.trim()"
            :active="searched && !loading"
          />
          <RelatedDiscussions
            :card-ids="cards.map((c) => c.id)"
            :cards="cards"
            :keyword="keyword.trim()"
            :active="searched && !loading"
          />
        </section>
        <SearchAdRail side="right" :items="adsRight" class="rail-right" @close="dismissAds" />
        <div v-if="adsMobile.length" class="rail-mobile">
          <div class="mobile-head">
            <p class="mobile-label">赞助产品</p>
            <button type="button" class="mobile-close" @click="dismissAds">关闭</button>
          </div>
          <div class="mobile-track">
            <a
              v-for="item in adsMobile"
              :key="'m' + item.id"
              class="mobile-ad"
              href="#"
              @click.prevent="openAd(item)"
            >
              <span class="ad-badge">广告</span>
              <strong>{{ item.title }}</strong>
              <span v-if="item.price_label">{{ item.price_label }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <div v-if="tray.length" class="compare-dock">
      <div class="dock-main">
        <p class="dock-label">对比 {{ tray.length }}/{{ maxCompare }}</p>
        <div class="dock-cards">
          <span v-for="card in tray" :key="card.id" class="dock-chip">
            <span class="dock-name">{{ card.name }}</span>
            <button type="button" aria-label="移出对比" @click="removeFromTray(card.id)">×</button>
          </span>
        </div>
      </div>
      <div class="dock-actions">
        <button type="button" class="dock-clear" @click="clearTray">清空</button>
        <button type="button" class="dock-go" :disabled="tray.length < 2" @click="goCompare">
          {{ tray.length < 2 ? '再选一款' : '查看对比' }}
        </button>
      </div>
    </div>
  </AppShell>
</template>

<script>
import Icon from '../components/Icon.vue'
import CreditNext from '../components/CreditNext.vue'
import PriceWorkspace from './PriceWorkspace.vue'
import BidWorkspace from './BidWorkspace.vue'
import AdviseWorkspace from './AdviseWorkspace.vue'
import QuoteAsk from '../components/QuoteAsk.vue'
import QueryProcessRail from '../components/QueryProcessRail.vue'
import SearchAdRail from '../components/SearchAdRail.vue'
import AppShell from '../layouts/AppShell.vue'
import RelatedDiscussions from '../components/RelatedDiscussions.vue'
import RelatedArticles from '../components/RelatedArticles.vue'
import {
  buildCatalogSearchSteps,
  createProcessRunner
} from '../utils/queryProcessScript'
import {
  catalogSearch,
  exportArtifactXlsx,
  listArticles,
  listBomComponents,
  listBomTemplates,
  listSearchAds,
  quoteServer
} from '../api/platformService'
import { notifyAuthChanged, onAuthChanged } from '../utils/authBus'
import { sessionRole } from '../utils/session'
import { hideAdsForAWhile, pickRandomAds, shouldShowAdsThisSearch } from '../utils/searchAds'
import {
  MAX_COMPARE,
  readTray,
  refreshTrayFromResults,
  removeTrayCard,
  toggleTrayCard,
  writeTray
} from '../utils/compareTray'
import { buildBomReport, downloadBlob } from '../utils/artifactReport'

export default {
  name: 'HomeWorkspace',
  components: {
    Icon,
    CreditNext,
    AppShell,
    RelatedDiscussions,
    RelatedArticles,
    SearchAdRail,
    PriceWorkspace,
    BidWorkspace,
    AdviseWorkspace,
    QuoteAsk,
    QueryProcessRail
  },
  data() {
    return {
      keyword: '',
      mode: 'search',
      tplOpen: false,
      pickedTpl: '',
      templateBoards: [],
      bidFile: null,
      loggedIn: Boolean(sessionRole()),
      domestic: false,
      memoryMin: '',
      fp16Min: '',
      fp8Min: '',
      vendor: '',
      examples: ['H100', '含光 800', 'B200', 'MI300', '昇腾'],
      cards: [],
      adsLeft: [],
      adsRight: [],
      tray: [],
      loading: false,
      searched: false,
      error: '',
      gateStatus: 0,
      chargeTip: '',
      preview: false,
      resultTotal: 0,
      maxCompare: MAX_COMPARE,
      bomQuote: null,
      bomError: '',
      bomGate: 0,
      bomLoading: false,
      bomDownloading: false,
      bomDownloadError: '',
      latestArticles: [],
      processSteps: [],
      processRunner: null,
      priceLoading: false,
      adviseLoading: false,
      bidLoading: false
    }
  },
  mounted() {
    this.offAuth = onAuthChanged(() => {
      this.loggedIn = Boolean(sessionRole())
    })
    this.tray = readTray()
    this.mode = this.modeFromQuery(this.$route.query?.mode)
    const q = this.$route.query?.q
    if (q) this.keyword = String(q)
    if (this.mode === 'search' && q) this.search()
    if (this.mode === 'bom') this.loadBomTemplates()
    this.loadLatestArticles()
    document.addEventListener('click', this.closeMode)
  },
  beforeUnmount() {
    this.stopProcessRunner()
    if (this.offAuth) this.offAuth()
    document.removeEventListener('click', this.closeMode)
  },
  watch: {
    '$route.query.mode'(value) {
      this.mode = this.modeFromQuery(value)
      if (this.mode !== 'search') this.stopProcessRunner()
      if (this.mode === 'bom') this.loadBomTemplates()
    }
  },
  computed: {
    hasSideAds() {
      return this.adsLeft.length > 0 || this.adsRight.length > 0
    },
    resultsLayoutClass() {
      return {
        'ads-left': this.adsLeft.length > 0,
        'ads-right': this.adsRight.length > 0
      }
    },
    adsMobile() {
      return [...this.adsLeft, ...this.adsRight].slice(0, 4)
    },
    trayFull() {
      return this.tray.length >= this.maxCompare
    },
    presetOn() {
      if (this.domestic && String(this.memoryMin) === '96' && !this.fp16Min && !this.fp8Min && !this.vendor) return 'domestic96'
      if (!this.domestic && !this.memoryMin && String(this.fp16Min) === '300' && !this.fp8Min && !this.vendor) return 'fp16_300'
      if (!this.domestic && !this.memoryMin && !this.fp16Min && String(this.fp8Min) === '1000' && !this.vendor) return 'fp8_1000'
      return ''
    },
    pageTitle() {
      return { search: '查参数', price: '卡价格', bom: '服务器估价', bid: '招投标分析', advise: '选型指导' }[this.mode] || '查参数'
    },
    modeHint() {
      if (this.mode === 'price') return '输入卡名，查看官方、市场和二手价。'
      if (this.mode === 'bom') return '在输入框选择模板，或写下预算和用途。'
      if (this.mode === 'bid') return '识别显存、FP16、FP8、功耗和形态，并对照卡库打分。优先国产不会排除进口卡。'
      if (this.mode === 'advise') return '描述需求。推荐只用卡库里的参数和价格。'
      return '输入卡名，或按显存、FP16、FP8 筛选。'
    },
    placeholder() {
      if (this.mode === 'price') return '例如 H100、昇腾 910'
      if (this.mode === 'bom') return '选择模板，或写下预算和用途'
      if (this.mode === 'bid') return '粘贴招标技术要求，至少 20 字'
      if (this.mode === 'advise') return '例如：国产，训练 70B，预算 80 万'
      return '例如 H100、昇腾、HBM3'
    },
    sendLabel() {
      if (this.mode === 'price') return this.priceLoading ? '查询中…' : '查价格'
      if (this.mode === 'bom') return this.bomLoading ? '估价中…' : '开始估价'
      if (this.mode === 'bid') return this.bidLoading ? '分析中…' : '开始分析'
      if (this.mode === 'advise') return this.adviseLoading ? '选型中…' : '开始选型'
      return this.loading ? '检索中…' : '搜索'
    },
    bomInquirySummary() {
      const quote = this.bomQuote
      if (!quote) return '服务器估价'
      const name = quote.template_name || '按组件组合'
      return `${name} ${quote.total || ''} ${quote.currency || ''}`.trim()
    },
    bomInquiryDetail() {
      const quote = this.bomQuote || {}
      return {
        text: this.keyword,
        understand: quote.understand || '',
        template_name: quote.template_name || '',
        total: quote.total || '',
        currency: quote.currency || '',
        items: (quote.items || []).map((item) => item.name).filter(Boolean)
      }
    },
    filterHint() {
      const parts = []
      if (this.vendor) parts.push(this.vendor)
      else if (this.domestic) parts.push('国产')
      if (this.memoryMin !== '' && this.memoryMin != null) parts.push(`显存≥${this.memoryMin}GB`)
      if (this.fp16Min !== '' && this.fp16Min != null) parts.push(`FP16≥${this.fp16Min}T`)
      if (this.fp8Min !== '' && this.fp8Min != null) parts.push(`FP8≥${this.fp8Min}T`)
      if (this.keyword.trim()) parts.push(`关键词「${this.keyword.trim()}」`)
      return parts.join(' · ')
    }
  },
  methods: {
    async downloadBomExcel() {
      if (!this.bomQuote || this.bomDownloading) return
      this.bomDownloading = true
      this.bomDownloadError = ''
      try {
        const report = buildBomReport({
          keyword: this.keyword.trim(),
          quote: this.bomQuote
        })
        if (!report?.exportPayload) throw new Error('无法生成表格')
        const blob = await exportArtifactXlsx(report.exportPayload)
        if (blob.type && blob.type.includes('application/json')) {
          const data = JSON.parse(await blob.text())
          throw new Error(data.error || '导出失败')
        }
        downloadBlob(report.filename || '服务器估价.xlsx', blob)
      } catch (err) {
        this.bomDownloadError = err.message || err.response?.data?.error || '下载失败'
      } finally {
        this.bomDownloading = false
      }
    },
    formatArticleTime(iso) {
      if (!iso) return ''
      return String(iso).replace('T', ' ').slice(0, 10)
    },
    async loadLatestArticles() {
      try {
        const data = await listArticles({ per_page: 5 })
        this.latestArticles = data.articles || []
      } catch (e) {
        this.latestArticles = []
      }
    },
    highlights(card) {
      return (card.attrs || []).slice(0, 3)
    },
    hiddenAttrCount(card) {
      const shown = (card.attrs || []).length
      const total = Number(card.attr_count || 0)
      return Math.max(0, total - shown)
    },
    rest(card) {
      return (card.attrs || []).slice(3)
    },
    inTray(id) {
      return this.tray.some((card) => card.id === id)
    },
    toggleTray(card) {
      this.tray = toggleTrayCard(card)
    },
    removeFromTray(id) {
      this.tray = removeTrayCard(id)
    },
    clearTray() {
      this.tray = writeTray([])
    },
    goCompare() {
      if (this.tray.length < 2) return
      this.$router.push({
        path: '/compare',
        query: { ids: this.tray.map((card) => card.id).join(',') }
      })
    },
    applyPreset(name) {
      const reset = () => {
        this.domestic = false
        this.memoryMin = ''
        this.fp16Min = ''
        this.fp8Min = ''
        this.vendor = ''
      }
      if (name === 'domestic96') {
        reset()
        this.domestic = true
        this.memoryMin = '96'
        this.search()
        return
      }
      if (name === 'fp16_300') {
        reset()
        this.fp16Min = '300'
        this.search()
        return
      }
      if (name === 'fp8_1000') {
        reset()
        this.fp8Min = '1000'
        this.search()
        return
      }
      if (name === 'ascend' || name === 'hygon' || name === 'enflame') {
        reset()
        this.domestic = true
        this.vendor = { ascend: '华为昇腾', hygon: '海光', enflame: '燧原科技' }[name]
        this.keyword = ''
        this.search()
        return
      }
      reset()
      this.keyword = ''
    },
    useExample(name) {
      this.keyword = name
      this.vendor = ''
      this.search()
    },
    stopProcessRunner() {
      if (this.processRunner) {
        this.processRunner.stop()
        this.processRunner = null
      }
    },
    startSearchProcess() {
      this.stopProcessRunner()
      const steps = buildCatalogSearchSteps({
        q: this.keyword,
        domestic: this.domestic,
        vendor: this.vendor,
        memoryMin: this.memoryMin,
        fp16Min: this.fp16Min,
        fp8Min: this.fp8Min
      })
      this.processSteps = steps
      this.processRunner = createProcessRunner(steps, {
        onUpdate: (next) => {
          this.processSteps = next
        }
      })
      this.processRunner.start()
    },
    clearSearch() {
      this.stopProcessRunner()
      this.processSteps = []
      this.keyword = ''
      this.domestic = false
      this.memoryMin = ''
      this.fp16Min = ''
      this.fp8Min = ''
      this.vendor = ''
      this.cards = []
      this.adsLeft = []
      this.adsRight = []
      this.searched = false
      this.error = ''
      this.chargeTip = ''
      this.gateStatus = 0
      this.preview = false
      this.resultTotal = 0
    },
    dismissAds() {
      hideAdsForAWhile()
      this.adsLeft = []
      this.adsRight = []
    },
    async loadAds() {
      this.adsLeft = []
      this.adsRight = []
      if (!shouldShowAdsThisSearch()) return
      try {
        const data = await listSearchAds({
          q: this.keyword.trim(),
          vendor: this.vendor || (this.domestic ? '国产' : '')
        })
        // 每侧最多随机 1 条，避免刷屏
        this.adsLeft = pickRandomAds(data.left || [], 1)
        this.adsRight = pickRandomAds(data.right || [], 1)
      } catch (e) {
        this.adsLeft = []
        this.adsRight = []
      }
    },
    openAd(item) {
      const url = item.url || ''
      if (!url) return
      if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank', 'noopener')
        return
      }
      this.$router.push(url)
    },
    modeFromQuery(value) {
      return ['price', 'bom', 'bid', 'advise'].includes(value) ? value : 'search'
    },
    closeMode() {
      this.tplOpen = false
    },
    async loadBomTemplates() {
      try {
        const [components, templates] = await Promise.all([listBomComponents(), listBomTemplates()])
        const byId = {}
        ;(components || []).forEach((row) => {
          byId[row.id] = row
        })
        this.templateBoards = (templates || []).map((tpl) => {
          let raw = []
          try {
            raw = JSON.parse(tpl.component_ids_json || '[]')
          } catch (e) {
            raw = []
          }
          const items = raw.map((id) => byId[Number(id)]).filter(Boolean)
          const total = items.reduce((sum, item) => {
            const matched = String(item.unit_price || '').replace(/,/g, '').match(/(\d+(?:\.\d+)?)/)
            return sum + (matched ? Number(matched[1]) : 0)
          }, 0)
          return {
            id: tpl.id,
            name: tpl.name,
            description: tpl.description || '',
            items,
            total: Math.round(total * 100) / 100,
            currency: (items[0] && items[0].currency) || 'CNY'
          }
        })
      } catch (e) {
        this.templateBoards = []
      }
    },
    async openTemplates() {
      this.tplOpen = !this.tplOpen
      if (this.tplOpen && !this.templateBoards.length) await this.loadBomTemplates()
    },
    catLabel(cat) {
      return { cpu: 'CPU', motherboard: '主板', memory: '内存', ssd: 'SSD', gpu: 'GPU' }[cat] || cat
    },
    pickTemplate(board) {
      this.pickedTpl = board.name
      this.keyword = board.name
      this.tplOpen = false
      this.bomError = ''
      this.bomGate = 0
      this.bomDownloadError = ''
      this.bomQuote = {
        template_name: board.name,
        note: board.description || '',
        items: board.items,
        total: board.total,
        currency: board.currency,
        charged: false
      }
    },
    onBidFile(event) {
      const file = event.target.files && event.target.files[0]
      this.bidFile = file || null
    },
    async send() {
      if (this.mode === 'search') return this.search()
      if (this.mode === 'price' && this.$refs.price) {
        this.$refs.price.keyword = this.keyword
        this.priceLoading = true
        try {
          return await this.$refs.price.search()
        } finally {
          this.priceLoading = false
        }
      }
      if (this.mode === 'bid' && this.$refs.bid) {
        this.$refs.bid.text = this.keyword
        this.$refs.bid.file = this.bidFile
        this.bidLoading = true
        let stopWatch = null
        try {
          await this.$refs.bid.submit()
          await this.$nextTick()
          await new Promise((resolve) => {
            stopWatch = this.$watch(
              () => {
                const ref = this.$refs.bid
                if (!ref) return true
                if (ref.error && !ref.job) return true
                if (!ref.job) return !ref.loading
                return !ref.isBusy
              },
              (done) => {
                if (done) resolve()
              },
              { immediate: true }
            )
          })
        } finally {
          if (typeof stopWatch === 'function') stopWatch()
          this.bidLoading = false
        }
        return
      }
      if (this.mode === 'advise' && this.$refs.advise) {
        this.$refs.advise.text = this.keyword
        this.adviseLoading = true
        let stopWatch = null
        try {
          await this.$refs.advise.submit()
          await this.$nextTick()
          await new Promise((resolve) => {
            stopWatch = this.$watch(
              () => {
                const ref = this.$refs.advise
                if (!ref) return true
                return Boolean(ref.result || ref.error || !ref.processActive)
              },
              (done) => {
                if (done) resolve()
              },
              { immediate: true }
            )
          })
        } finally {
          if (typeof stopWatch === 'function') stopWatch()
          this.adviseLoading = false
        }
        return
      }
      if (this.mode === 'bom') return this.quoteBom()
    },
    async quoteBom() {
      const text = this.keyword.trim()
      const board = this.templateBoards.find((item) => item.name === text)
      if (board) {
        this.pickTemplate(board)
        return
      }
      if (!text) {
        this.bomError = '请写下预算和用途'
        this.bomGate = 0
        this.bomQuote = null
        return
      }
      this.bomLoading = true
      this.bomError = ''
      this.bomGate = 0
      this.bomQuote = null
      this.bomDownloadError = ''
      try {
        const data = await quoteServer(text)
        this.bomQuote = data
        if (data.charged) notifyAuthChanged()
      } catch (err) {
        const status = err.response?.status
        this.bomGate = status === 401 || status === 402 ? status : 0
        this.bomError = err.response?.data?.error || '估价没有完成'
      } finally {
        this.bomLoading = false
      }
    },
    scrollToResults() {
      this.$nextTick(() => {
        const el = document.getElementById('search-results')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    async search() {
      const q = this.keyword.trim()
      const mem = this.memoryMin === '' || this.memoryMin == null ? null : Number(this.memoryMin)
      const fp16 = this.fp16Min === '' || this.fp16Min == null ? null : Number(this.fp16Min)
      const fp8 = this.fp8Min === '' || this.fp8Min == null ? null : Number(this.fp8Min)
      const hasFilter =
        this.domestic
        || Boolean(this.vendor)
        || (mem != null && !Number.isNaN(mem))
        || (fp16 != null && !Number.isNaN(fp16))
        || (fp8 != null && !Number.isNaN(fp8))
      if (!q && !hasFilter) return
      this.loading = true
      this.error = ''
      this.gateStatus = 0
      this.chargeTip = ''
      this.preview = false
      this.resultTotal = 0
      this.searched = true
      this.cards = []
      this.startSearchProcess()
      this.scrollToResults()
      try {
        const params = { q }
        if (this.domestic) params.domestic = 1
        if (this.vendor) params.vendor = this.vendor
        if (mem != null && !Number.isNaN(mem)) params.memory_gb_min = mem
        if (fp16 != null && !Number.isNaN(fp16)) params.fp16_tflops_min = fp16
        if (fp8 != null && !Number.isNaN(fp8)) params.fp8_tflops_min = fp8
        const data = await catalogSearch(params)
        this.cards = data.cards || []
        this.tray = refreshTrayFromResults(this.cards)
        this.preview = Boolean(data.preview)
        this.resultTotal = data.total != null ? data.total : this.cards.length
        if (data.preview) {
          this.chargeTip = ''
        } else if (data.charged && data.charge_delta) {
          this.chargeTip = `本次查询已扣 ${data.charge_delta} 积分`
        } else if (!this.cards.length) {
          this.chargeTip = '无匹配结果，未扣积分'
        } else if (data.role === 'user') {
          this.chargeTip = '相同条件重复查询不重复扣费'
        } else if (data.role === 'admin') {
          this.chargeTip = '管理员查询不扣积分'
        }
        await this.loadAds()
        notifyAuthChanged()
      } catch (err) {
        this.cards = []
        this.adsLeft = []
        this.adsRight = []
        this.processSteps = []
        this.stopProcessRunner()
        const status = err.response?.status
        this.gateStatus = status === 401 || status === 402 ? status : 0
        this.error = err.response?.data?.error || '查询失败'
        if (status === 402) {
          this.error = err.response?.data?.error || '积分不足，无法查询'
        }
      } finally {
        if (this.processRunner) this.processRunner.finish()
        this.processRunner = null
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.latest-articles {
  max-width: 720px;
  margin: 8px auto 20px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 16px;
  text-align: left;
}
.latest-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 10px;
}
.latest-head h2 { margin: 0; font-size: 16px; }
.latest-head a { color: #165dff; text-decoration: none; font-size: 13px; }
.latest-head a:hover { text-decoration: underline; }
.latest-articles ul { list-style: none; margin: 0; padding: 0; }
.latest-articles li {
  padding: 8px 0;
  border-top: 1px solid #f2f3f5;
  display: grid;
  gap: 2px;
}
.latest-articles li:first-child { border-top: 0; }
.latest-articles a { color: #165dff; text-decoration: none; font-weight: 600; }
.latest-articles a:hover { text-decoration: underline; }
.latest-articles .meta {
  color: #86909c; font-size: 12px; line-height: 1.5;
  display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden;
}
.hero { text-align: center; padding: 28px 0 8px; }
h1 { margin: 0 0 8px; font-size: 40px; font-weight: 600; letter-spacing: -0.03em; }
.sub { margin: 0 auto 20px; max-width: 720px; color: #86909c; font-size: 15px; }
.dialog {
  max-width: 760px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(29, 33, 41, 0.06);
  text-align: left;
}
.dialog textarea {
  display: block;
  width: 100%;
  border: 0;
  resize: none;
  padding: 16px 18px 6px;
  font: inherit;
  font-size: 15px;
  line-height: 1.5;
  color: #1d2129;
  background: transparent;
  box-sizing: border-box;
}
.dialog textarea:focus { outline: none; }
.dialog-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px 10px;
}
.mode-pick { position: relative; }
.mode-btn, .send, .attach {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e5e6eb;
  background: #f7f8fa;
  color: #1d2129;
  border-radius: 999px;
  height: 34px;
  padding: 0 12px;
  font-size: 13px;
  cursor: pointer;
}
.mode-btn:hover, .attach:hover { border-color: #165dff; color: #165dff; }
.caret { transform: rotate(90deg); font-size: 12px; }
.mode-btn[aria-expanded="true"] .caret { transform: rotate(-90deg); }
.send {
  margin-left: auto;
  border: 0;
  background: #165dff;
  color: #fff;
  font-weight: 600;
  padding: 0 16px;
}
.send:hover { background: #0e42d2; }
.mode-menu {
  position: absolute;
  left: 0;
  top: 40px;
  z-index: 8;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(29, 33, 41, 0.14);
  min-width: 220px;
}
.mode-menu button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  border-radius: 12px;
  padding: 8px 10px;
  cursor: pointer;
  color: #1d2129;
}
.mode-copy { display: grid; gap: 2px; }
.mode-copy strong { font-size: 14px; font-weight: 600; }
.mode-copy small { color: #86909c; font-size: 12px; }
.mode-menu button.on, .mode-menu button:hover { background: #f2f7ff; }
.tpl-menu { min-width: 240px; max-height: 280px; overflow: auto; }
.tpl-empty { display: block; padding: 8px 10px; color: #86909c; font-size: 13px; }
.mode-btn { max-width: 180px; }
.mode-btn { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.attach { position: relative; overflow: hidden; color: #4e5969; max-width: 180px; }
.attach input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
.bid-login {
  margin: 10px 0 0;
  font-size: 13px;
  color: #4e5969;
}
.bid-login a { color: #165dff; }
.results.narrow { max-width: 760px; margin: 16px auto 0; }
.stage {
  max-width: 760px;
  margin: 16px auto 0;
  text-align: left;
}
.bom-result {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 16px;
  padding: 16px 18px;
  margin-bottom: 12px;
}
.bom-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.bom-head h2 { margin: 0; }
.bom-dl {
  border: 1px solid #e5e6eb;
  background: #fff;
  color: #1d2129;
  border-radius: 10px;
  padding: 6px 12px;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  flex-shrink: 0;
}
.bom-dl:hover { border-color: #165dff; color: #165dff; }
.bom-dl:disabled { opacity: 0.55; cursor: not-allowed; }
.bom-dl-err { text-align: left; padding: 0; margin: 0 0 8px; }
.bom-result h2 { margin: 0; font-size: 18px; }
.bom-lead, .bom-note { margin: 0 0 8px; color: #4e5969; font-size: 14px; }
.bom-result ul { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }
.bom-result li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  background: #f7f8fa;
  border-radius: 12px;
  padding: 8px 10px;
  font-size: 13px;
}
.tool-panel {
  max-width: 760px;
  margin: 12px auto 0;
  padding: 14px 14px 12px;
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 16px;
  text-align: left;
}
.filters, .presets, .hot-keys {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.presets, .hot-keys { margin-top: 10px; }
.filters { color: #4e5969; font-size: 13px; }
.filters input[type=number] {
  width: 72px;
  border: 1px solid #e5e6eb;
  border-radius: 12px;
  padding: 6px 8px;
  margin: 0 4px;
}
.check { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: #4e5969; }
.label { color: #86909c; font-size: 13px; }
.chip {
  border: 1px solid #e5e6eb;
  background: #fff;
  border-radius: 999px;
  padding: 6px 12px;
  cursor: pointer;
  color: #4e5969;
}
.chip:hover { color: #165dff; border-color: #165dff; }
.chip.on { color: #165dff; border-color: #165dff; background: #e8f3ff; font-weight: 600; }
.filter-hint { margin: 10px 0 0; color: #86909c; font-size: 12px; }
.error { color: #f53f3f; }
.section-head { display: flex; gap: 12px; align-items: baseline; margin: 18px 0; flex-wrap: wrap; }
.section-head h2 { margin: 0; font-size: 20px; }
.result-line { color: #86909c; font-size: 13px; }
.result-line b { color: #165dff; }
.charge { color: #188038; font-size: 12px; }
.preview-note, .preview-more {
  margin: 0 0 14px;
  font-size: 13px;
  color: #4e5969;
  background: #fff7e8;
  border: 1px solid #ffe4ba;
  border-radius: 12px;
  padding: 8px 12px;
}
.preview-more { margin-top: 12px; }
.preview-note a, .preview-more a { color: #165dff; text-decoration: none; }
.preview-note a:hover, .preview-more a:hover { text-decoration: underline; }
.head-actions { margin-left: auto; display: flex; flex-wrap: wrap; gap: 6px; }
.clear {
  border: 1px solid #e5e6eb; background: #fff; border-radius: 999px; padding: 6px 12px; cursor: pointer;
}
.clear:disabled { opacity: 0.45; cursor: not-allowed; }
.result {
  background: #fff; border: 1px solid #e5e6eb; border-radius: 16px; padding: 20px; margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,.04);
}
.title-row { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-bottom: 14px; }
.compare-add {
  margin-left: auto;
  border: 1px solid #165dff;
  background: #fff;
  color: #165dff;
  border-radius: 12px;
  padding: 4px 10px;
  font-size: 13px;
  cursor: pointer;
}
.compare-add.on { background: #e8f3ff; font-weight: 600; }
.compare-add:disabled { opacity: 0.45; cursor: not-allowed; }
.compare-dock {
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: 40;
  width: min(880px, calc(100% - 24px));
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #e5e6eb;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
}
.dock-main { min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.dock-label { margin: 0; font-size: 12px; color: #86909c; }
.dock-cards { display: flex; flex-wrap: wrap; gap: 6px; }
.dock-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dock-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 220px;
  padding: 2px 6px 2px 8px;
  background: #f2f3f5;
  border-radius: 999px;
  font-size: 12px;
  color: #1d2129;
}
.dock-chip button {
  border: 0;
  background: transparent;
  color: #86909c;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0 2px;
}
.dock-actions { display: flex; gap: 8px; flex-shrink: 0; }
.dock-clear, .dock-go {
  border-radius: 12px;
  height: 32px;
  padding: 0 12px;
  cursor: pointer;
  font-size: 13px;
}
.dock-clear { border: 1px solid #e5e6eb; background: #fff; color: #4e5969; }
.dock-go { border: 0; background: #165dff; color: #fff; font-weight: 600; }
.dock-go:disabled { opacity: 0.45; cursor: not-allowed; }
.result h3 { margin: 0; font-size: 20px; }
.badges { display: flex; flex-wrap: wrap; gap: 6px; }
.badge {
  font-size: 12px; color: #4e5969; background: #f2f3f5; border-radius: 999px; padding: 2px 8px;
}
.badge.on { color: #165dff; background: #e8f3ff; font-weight: 600; }
.spec-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 12px; }
.spec-card { background: #f7f8fa; border-radius: 12px; padding: 12px; }
.spec-card .k { color: #86909c; font-size: 12px; }
.spec-card .v { margin-top: 4px; font-weight: 600; }
.spec-card .v.blue { color: #165dff; }
.specs { width: 100%; border-collapse: collapse; }
.specs th, .specs td { border-top: 1px solid #e5e6eb; padding: 8px 6px; text-align: left; font-size: 14px; }
.specs th { width: 140px; color: #4e5969; font-weight: 500; }
.discuss-row { margin-top: 12px; }
.discuss {
  font-size: 13px; color: #165dff; text-decoration: none;
}
.discuss:hover { text-decoration: underline; }
.empty, .error { text-align: center; color: #86909c; padding: 28px 0; }
.error { color: #d3382c; }
.error a { margin-left: 8px; color: #165dff; }
.results-layout {
  display: block;
}
.results-main { min-width: 0; }
.results-layout.ads-left,
.results-layout.ads-right {
  display: grid;
  gap: 16px;
  align-items: start;
}
.results-layout.ads-left.ads-right {
  grid-template-columns: 220px minmax(0, 1fr) 220px;
}
.results-layout.ads-left:not(.ads-right) {
  grid-template-columns: 220px minmax(0, 1fr);
}
.results-layout.ads-right:not(.ads-left) {
  grid-template-columns: minmax(0, 1fr) 220px;
}
.results-layout.ads-left.ads-right .rail-left { grid-column: 1; }
.results-layout.ads-left.ads-right .results-main,
.results-layout.ads-left:not(.ads-right) .results-main { grid-column: 2; }
.results-layout.ads-left:not(.ads-right) .rail-left { grid-column: 1; }
.results-layout.ads-right:not(.ads-left) .results-main { grid-column: 1; }
.results-layout.ads-left.ads-right .rail-right { grid-column: 3; }
.results-layout.ads-right:not(.ads-left) .rail-right { grid-column: 2; }
.rail-mobile {
  display: none; grid-column: 1 / -1; opacity: 0.72;
}
.mobile-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.mobile-label { margin: 0; font-size: 12px; color: #a9aeb8; }
.mobile-close {
  border: 0; background: transparent; color: #a9aeb8; font-size: 11px; cursor: pointer; padding: 0;
}
.mobile-track {
  display: flex; gap: 10px; overflow-x: auto; padding-bottom: 4px;
}
.mobile-ad {
  flex: 0 0 200px; display: grid; gap: 4px; padding: 10px 12px;
  background: rgba(247, 248, 250, 0.85); border: 1px dashed #d0d3d9; border-radius: 12px;
  text-decoration: none; color: #4e5969; font-size: 13px;
}
.mobile-ad .ad-badge {
  justify-self: start; font-size: 10px; color: #a9aeb8;
  border: 1px solid #d0d3d9; border-radius: 2px; padding: 0 4px;
}
@media (max-width: 1100px) {
  .results-layout.ads-left.ads-right,
  .results-layout.ads-right:not(.ads-left) {
    grid-template-columns: minmax(0, 1fr) 220px;
  }
  .results-layout.ads-left:not(.ads-right) {
    grid-template-columns: minmax(0, 1fr);
  }
  .rail-left { display: none; }
  .results-layout.ads-left.ads-right .results-main,
  .results-layout.ads-left:not(.ads-right) .results-main,
  .results-layout.ads-right:not(.ads-left) .results-main { grid-column: 1; }
  .results-layout.ads-left.ads-right .rail-right,
  .results-layout.ads-right:not(.ads-left) .rail-right { grid-column: 2; }
}
@media (max-width: 840px) {
  .results-layout.ads-left.ads-right,
  .results-layout.ads-left:not(.ads-right),
  .results-layout.ads-right:not(.ads-left) {
    grid-template-columns: minmax(0, 1fr);
  }
  .rail-right { display: none; }
  .results-layout.ads-left.ads-right .results-main,
  .results-layout.ads-left:not(.ads-right) .results-main,
  .results-layout.ads-right:not(.ads-left) .results-main { grid-column: 1; }
  .rail-mobile { display: block; margin-top: 8px; }
}
@media (max-width: 720px) {
  h1 { font-size: 26px; }
  .spec-cards { grid-template-columns: 1fr; }
  .compare-dock { flex-direction: column; align-items: stretch; }
  .dock-actions { justify-content: flex-end; }
}
</style>
